// Last-resort boot failure surface.
//
// WHY THIS MODULE EXISTS AT ALL: several modules in the import graph run
// fail-closed configuration guards at MODULE SCOPE — `oidc-config.ts` and
// `api/platform-graph.ts` both call into `config/customer-deployment.ts`,
// which throws when a production bundle was built without
// VITE_KEYCLOAK_AUTHORITY / VITE_PLATFORM_GRAPH_BASE_URL. That throw happens
// while the browser is still EVALUATING the module graph, so `main.tsx`'s own
// body — including its `bootstrap().catch(...)` — never runs. The symptom is
// the worst possible one: an empty <div id="root">, a silent console (a
// module-evaluation throw surfaces as a window `error` event, not a
// console.error), and zero network activity. Nothing on screen says why.
//
// So the listeners below are installed from a module that is imported FIRST in
// main.tsx (import order is load-bearing — see the comment there) and depends
// on nothing but the DOM. They catch BOTH failure shapes:
//   * a throw during module-graph evaluation  → window "error"
//   * a rejected bootstrap() promise          → window "unhandledrejection"
//     (main.tsx also wires reportBootFailure explicitly as its .catch)
//
// The copy is NOT translated. It cannot be: i18next lives further down the
// same import graph and initI18n() is one of the things that may have failed.
// A two-locale literal picked off <html lang>/navigator is the honest maximum
// here, and this is the only place in the app allowed to hardcode UI text.

let booted = false;
let painted = false;

/**
 * Called by main.tsx immediately after `createRoot().render()`. From this
 * point on the app owns the screen and later errors belong to React's route
 * error boundaries (`components/route-boundaries.tsx`), not to this module.
 */
export function markBootComplete(): void {
  booted = true;
}

function isTurkish(): boolean {
  const lang =
    document.documentElement.lang !== ""
      ? document.documentElement.lang
      : (navigator.language ?? "");
  return lang.toLowerCase().startsWith("tr");
}

function paint(): void {
  const root = document.getElementById("root");
  // An already-populated #root means the app did mount and something else
  // failed later — leave that to the route error boundaries.
  if (root === null || root.innerHTML !== "") return;
  painted = true;

  const tr = isTurkish();
  const container = document.createElement("div");
  // Layout only — no colours. The stylesheet is a separate <link> and loads
  // independently of the JS that just died, so body's bg/fg classes still
  // apply; hardcoding colours here would both duplicate and outlive tokens.
  container.setAttribute(
    "style",
    "max-width:32rem;margin:4rem auto;padding:0 1.5rem;text-align:center;font-family:inherit",
  );

  const heading = document.createElement("h1");
  heading.setAttribute("style", "font-size:1.25rem;font-weight:600;margin-bottom:0.75rem");
  heading.textContent = tr ? "Uygulama başlatılamadı" : "The application failed to start";

  const body = document.createElement("p");
  body.setAttribute("style", "margin-bottom:1.5rem;line-height:1.6");
  body.textContent = tr
    ? "Bu genellikle eksik veya hatalı bir dağıtım yapılandırmasından kaynaklanır. Teknik ayrıntı tarayıcı konsoluna yazıldı."
    : "This is usually a missing or invalid deployment configuration. The technical detail was written to the browser console.";

  const reload = document.createElement("button");
  reload.type = "button";
  reload.setAttribute(
    "style",
    "border:1px solid currentColor;border-radius:0.5rem;padding:0.5rem 1rem;cursor:pointer;background:transparent;color:inherit;font:inherit",
  );
  reload.textContent = tr ? "Yeniden dene" : "Try again";
  reload.addEventListener("click", () => {
    window.location.reload();
  });

  container.append(heading, body, reload);
  root.replaceChildren(container);
}

/**
 * Log the technical detail and paint the fallback surface once. The detail is
 * deliberately console-only: it can name deployment configuration
 * (`VITE_KEYCLOAK_AUTHORITY is required in production`), which belongs in an
 * operator's console and logs rather than on an end user's screen.
 */
export function reportBootFailure(detail: unknown): void {
  if (booted || painted) return;
  console.error("customer-web: boot failed before the app could mount", detail);
  paint();
}

window.addEventListener("error", (event: ErrorEvent) => {
  // Resource-load failures (a 404'd <img>) also fire "error" on window, but
  // they bubble from the element and carry no `error`. Only module/script
  // evaluation failures reach us with a thrown value attached.
  if (event.error === undefined || event.error === null) return;
  reportBootFailure(event.error);
});

window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
  reportBootFailure(event.reason);
});
